import React, {Fragment} from "react";
import {observer} from "mobx-react";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import {type IModalH2hChallengeController} from "views/components/modals/modal_h2h_challenge/modal_h2h_challenge.controller";
import {Modal, IconButton, Typography, Button} from "@mui/material";
import {ModalContent, ModalCloseBlock} from "views/components/modals/common";
import CloseIcon from "@mui/icons-material/Close";
import styled from "@emotion/styled";
import {LevelBadge} from "views/components/level_badge/level_badge.component";
import {GameLevel} from "data/enums";
import {motion} from "framer-motion";
import {times} from "lodash";
import opponentUnknownImage from "assets/img/opponent.png";
import {Exist} from "views/components/exist/exist.component";
import {H2hLevelBadge} from "views/components/h2h_level_badge/h2h_level_badge.component";
import ErrorIcon from "@mui/icons-material/Error";
import {NavLink} from "react-router-dom";

const Content = styled.div`
	width: 100%;
	display: flex;
	flex-flow: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	p {
		color: #ffffff;
		text-align: center;
		font-size: 16px;
		font-style: normal;
		font-weight: 400;
		line-height: 140%; /* 22.4px */

		b {
			font-weight: bold;
		}
	}

	button,
	a {
		max-width: unset;
		margin-top: 16px;
	}
`;

const Challenges = styled.div`
	display: flex;
	align-items: center;
`;

const Vs = styled.div`
	padding: 0 12px;
	color: #fff;
	text-align: center;
	font-size: 24px;
	font-style: normal;
	font-weight: 800;
	line-height: 140%;
`;

const ChallengeUser = styled.div`
	border-radius: 8px;
	border: 4px solid #62676e;
	background: #090b0f;
	display: flex;
	flex-flow: column;
	gap: 4px;
	width: 128px;
	height: 128px;
	overflow: hidden;
`;

const UserItem = styled.div`
	border-radius: 8px;
	border: 4px solid #62676e;
	background: #090b0f;
	display: flex;
	flex-flow: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
	width: 128px;
	height: 128px;
	overflow: hidden;

	& > svg {
		width: 60px;
		height: 60px;
		color: var(--invalidColor);
	}
`;

const AnimatedStack = styled.div`
	display: flex;
	flex-flow: row;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: center;

	&.animate {
		animation: geniusScrollList 3s linear infinite;
	}

	&.stopAnimate {
		animation: geniusScrollList 0.3s linear forwards;
	}
`;

const AnimationItem = styled(motion.div)`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-flow: column;
	gap: 4px;
	flex: 1 1 100%;
	width: 120px;
	height: 120px;
	opacity: 0;
	animation: geniusFadeIn 0.3s ease forwards;
	img {
		width: 60px;
		height: 60px;
	}

	& > p {
		filter: blur(3px);
	}
`;

const LoadingStatusTitle = styled.div`
	font-size: 20px;
	font-style: normal;
	font-weight: 600;
	line-height: 140%;
	padding: 16px 10px;
`;
export const ModalH2hChallenge: React.FC = observer(() => {
	const {isOpen, close, animation, randomUsers, lastUser, opponentGameBar, errorMessage} =
		useViewController<IModalH2hChallengeController>(Bindings.ModalH2hChallengeController);

	return (
		<Modal open={isOpen}>
			<Fragment>
				<ModalContent>
					<ModalCloseBlock>
						<IconButton sx={{minWidth: 0}} onClick={() => void close()}>
							<CloseIcon color={"inherit"} />
						</IconButton>
					</ModalCloseBlock>

					<Content>
						<Challenges>
							<UserItem>
								<LevelBadge level={GameLevel.SINGLE_A} size={60} />
								<Typography variant={"body2"} fontWeight={"bold"}>
									You
								</Typography>
							</UserItem>
							<Vs>VS</Vs>
							<Exist when={!errorMessage.length}>
								<ChallengeUser>
									{times(2, (n) => n).map((_, index) => (
										<AnimatedStack
											key={`${animation}${index}`}
											className={animation}>
											<Exist when={opponentGameBar}>
												<AnimationItem key={"opponentGameBar"}>
													<Exist when={index === 0}>
														<H2hLevelBadge
															level={
																lastUser?.level ||
																GameLevel.SINGLE_A
															}
															isFriend={lastUser?.isFriend}
															isBot={lastUser?.isBot}
															size={60}
														/>
														<Typography
															component={"p"}
															variant={"body2"}
															fontWeight={"bold"}>
															{lastUser?.userName}
														</Typography>
													</Exist>
													<Exist when={index === 1}>
														<H2hLevelBadge
															level={
																opponentGameBar?.level ||
																GameLevel.SINGLE_A
															}
															isFriend={opponentGameBar?.isFriend}
															isBot={opponentGameBar?.isBot}
															size={60}
														/>
														<Typography
															component={"span"}
															variant={"body2"}
															fontWeight={"bold"}>
															{opponentGameBar?.userName}
														</Typography>
													</Exist>
												</AnimationItem>
											</Exist>
											<Exist when={!opponentGameBar}>
												<AnimationItem key={"userIndex"}>
													<img src={opponentUnknownImage} alt={""} />
													<Typography
														component={"p"}
														variant={"body2"}
														fontWeight={"bold"}>
														Opponent
													</Typography>
												</AnimationItem>
												{randomUsers.map((user, userIndex) => (
													<AnimationItem key={userIndex}>
														<H2hLevelBadge
															level={user.level || GameLevel.SINGLE_A}
															size={60}
															isBot={user.isBot}
															isFriend={user.isFriend}
														/>
														<Typography
															component={"p"}
															variant={"body2"}
															fontWeight={"bold"}>
															{user.userName}
														</Typography>
													</AnimationItem>
												))}
											</Exist>
										</AnimatedStack>
									))}
								</ChallengeUser>
							</Exist>

							<Exist when={errorMessage.length}>
								<UserItem>
									<ErrorIcon />
									<Typography variant={"body2"} fontWeight={"bold"}>
										Error
									</Typography>
								</UserItem>
							</Exist>
						</Challenges>

						<LoadingStatusTitle>
							<Exist when={!errorMessage.length}>
								<Exist when={opponentGameBar}>You have an opponent!</Exist>
								<Exist when={!opponentGameBar}>Opponent loading...</Exist>
							</Exist>

							<Exist when={errorMessage.length}>{errorMessage}</Exist>
						</LoadingStatusTitle>

						<Button
							variant="contained"
							disabled={!opponentGameBar}
							color="secondary"
							component={NavLink}
							to={`/head_to_head/games/${String(opponentGameBar?.id || 0)}`}
							onClick={close}
							sx={{
								width: "auto",
								textTransform: "none",
							}}>
							Play now
						</Button>
					</Content>
				</ModalContent>
			</Fragment>
		</Modal>
	);
});
